import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SORTING } from "@/constants/sorting";
import { AppRoutes } from "@/routes/config";

// match.createdAt は登録日列を削除した後も残す:
// BE のデフォルトソートが match.createdAt|DESC のままで、この値が URL に載りうるため
const VALID_MATCH_SORT_FIELDS = ["match.matchLicenseNumber", "match.matchName", "match.contractStartDate", "match.createdAt"] as const;

const MATCH_LIST_PAGES = [
  AppRoutes.duringContractList,
  AppRoutes.canceledContractList,
  AppRoutes.trialMatchList,
  AppRoutes.trialCancelledList,
] as const;

export const isValidMatchSortValue = (sortValue: string): boolean => {
  if (!sortValue) return true;

  const parts = sortValue.split("|");
  if (parts.length !== 2) return false;

  const [fieldName, direction] = parts;

  const isValidField = VALID_MATCH_SORT_FIELDS.includes(fieldName as (typeof VALID_MATCH_SORT_FIELDS)[number]);

  const isValidDirection = direction === SORTING.ASC || direction === SORTING.DESC;

  return isValidField && isValidDirection;
};

export const useIsMatchSortValid = (): boolean => {
  const { pathname, search } = useLocation();

  return useMemo(() => {
    if (!MATCH_LIST_PAGES.includes(pathname as (typeof MATCH_LIST_PAGES)[number])) {
      return true;
    }

    const query = new URLSearchParams(search);
    const sortsParams = query.getAll("sorts");

    if (sortsParams.length === 0) return true;

    return sortsParams.every((sort) => isValidMatchSortValue(sort));
  }, [pathname, search]);
};

export const useMatchSortValidation = (): { isValidSort: boolean } => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isValidSort = useIsMatchSortValid();

  useEffect(() => {
    if (!isValidSort) {
      navigate(pathname, { replace: true });
    }
  }, [isValidSort, pathname, navigate]);

  return { isValidSort };
};
